﻿import React, { useState } from 'react';
//Utilities
import { useQuery } from '@tanstack/react-query';
import { ExecuteSearch } from '../../api/components';
//Components
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Typography,
  ButtonBase,
} from '@mui/material';
import { DebouncedTextField } from '../Inputs/TextFields/DebouncedInput';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { fetchSignedURL } from '../../api/file';
import { useLinkTypes } from '../../hooks/useLinkTypes';
import CircularProgress from '@mui/material/CircularProgress';
import {
  drawerSearchTriggerStyles,
  headerIconButtonStyles,
} from '../Navigation/Components/StyledComponents';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: 'absolute' }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

// `fullWidth` is the drawer presentation: the mobile and medium drawers give search its
// own row, where an icon-only circle reads as a stray dot. Both variants open the same
// modal — only the trigger's footprint differs.
export default function SearchBar({ fullWidth = false }) {
  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState('');

  const searchQuery = useQuery({
    queryKey: ['searchQuery', searchText],
    queryFn: async () => await ExecuteSearch({ SearchText: searchText })(),
    enabled: searchText !== '',
  });

  const linkTypeQuery = useLinkTypes();

  const [fetchFileError, setFetchFileError] = useState(null);
  const handleSecuredFile = async (path) => {
    try {
      await fetchSignedURL(path);
    } catch (error) {
      setFetchFileError(error.Message || 'Something went wrong');
    }
  };

  function OpenSearchLink(link) {
    let tmpLink = link.linkLocation;
    let newLink = tmpLink.substring(tmpLink.lastIndexOf('/') + 1);
    let pathName = `${process.env.REACT_APP_AGENTPORTAL_PREFIX}/${link.foundType}/${link.linkLocation}`;

    //let url = link.linkType == 2 ? fileLocation + newLink : newLink;
    if (link.linkType === linkTypeQuery.LINKTYPE.LINK || link.linkType === null) {
      window.open(link.linkLocation, 'self');
    } else {
      handleSecuredFile(pathName);
    }
  }

  if (linkTypeQuery.isLoading) return <CircularProgress size="1em" />;

  const closeModal = () => setOpenModal(false);
  const results = searchQuery.data;
  const hasResults = Array.isArray(results) && results.length > 0;

  return (
    <>
      {fullWidth ? (
        <ButtonBase
          aria-label="Search"
          onClick={() => setOpenModal(true)}
          sx={drawerSearchTriggerStyles}
        >
          <SearchIcon fontSize="small" />
          <Typography component="span" sx={{ fontSize: '1rem' }}>
            Search...
          </Typography>
        </ButtonBase>
      ) : (
        <IconButton
          aria-label="Search"
          onClick={() => setOpenModal(true)}
          sx={headerIconButtonStyles}
        >
          <SearchIcon />
        </IconButton>
      )}
      {/* The dialog owns the input, so it must render on the trigger alone — gating it on
          a populated result set (as the inline bar did) would mean the button could never
          open anything before a search had already run. */}
      <BootstrapDialog
        fullWidth={true}
        maxWidth={'lg'}
        onClose={closeModal}
        aria-labelledby="customized-dialog-title"
        open={openModal}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={closeModal}>
          Search
        </BootstrapDialogTitle>
        <DebouncedTextField
          // The rule guards against focus moving without the user asking. Here the dialog
          // opens only on the search button, and this field is the reason it opened.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          delayTimeout={500}
          value={searchText}
          variant="outlined"
          placeholder="Search..."
          label="Search Bar"
          sx={{ margin: '1em' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
        />
        <DialogContent dividers>
          {hasResults ? (
            <TableContainer
              component={Paper}
              style={{ overflowY: 'auto', overflowX: 'hidden', width: '100%' }}
            >
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow sx={{ margin: '1em 0', backgroundColor: 'white', fontWeight: 'bold' }}>
                    <TableCell>Item</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((s) => (
                    <TableRow
                      key={s.pID}
                      onClick={() => {
                        OpenSearchLink(s);
                      }}
                    >
                      <TableCell style={{ color: 'blue' }}>
                        <Link>{s.item} </Link>
                      </TableCell>
                      <TableCell>{s.subDescription}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ color: 'text.secondary' }}>
              {searchQuery.isFetching
                ? 'Searching…'
                : searchText === ''
                  ? 'Type to search.'
                  : 'No results found.'}
            </Typography>
          )}
        </DialogContent>
      </BootstrapDialog>
    </>
  );
}

SearchBar.propTypes = {
  fullWidth: PropTypes.bool,
};
